import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { InventarioTour } from "@/components/ProductTour";
import { CrudEmptyState, CrudShellHeader, Loading } from "@/components/ds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiGetJson, apiSendJson, clientRequestHeaders } from "@/lib/sm-api";
import { RenataLauncher } from "@/components/renata/RenataLauncher";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useWorkspaceLocationId } from "@/lib/location-context";
import { toast } from "sonner";
import { Check, ChevronsUpDown, Download, Loader2, ScanBarcode, Upload } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { WorkspaceHub, WorkspaceHubBack } from "@/components/workspace/WorkspaceHub";
import { getInventarioHubLinks } from "@/lib/workspace-hubs";
import { useTenantContextSafe } from "@/lib/tenant-context";
import {
  describeNicheStockImpact,
  inventoryControlsIngredients,
  inventoryPageSubtitle,
  inventoryPageTitle,
  resolveStockControlMode,
} from "@/lib/stock-model";
import { Link } from "@tanstack/react-router";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";

const TABS = ["hub", "movimientos", "stock", "historial", "exportar", "ajustes"] as const;
type InventarioTab = (typeof TABS)[number];

const LEGACY_TAB_REDIRECT: Record<string, { to: string; search?: Record<string, string> }> = {
  marcas: { to: "/app/marcas" },
  precios: { to: "/app/catalogacion/precios" },
  productos: { to: "/app/catalogacion/importar" },
  devoluciones: { to: "/app/ordenes/devoluciones" },
};

export const Route = createFileRoute("/app/inventario")({
  validateSearch: (s: Record<string, unknown>) => {
    const raw = typeof s.tab === "string" ? s.tab : "hub";
    const tab = TABS.includes(raw as InventarioTab) ? (raw as InventarioTab) : "hub";
    return { tab };
  },
  beforeLoad: ({ search }) => {
    const tab = typeof search.tab === "string" ? search.tab : "";
    const dest = LEGACY_TAB_REDIRECT[tab];
    if (dest) throw redirect({ to: dest.to, search: dest.search });
  },
  component: InventarioPage,
  head: () => ({ meta: [{ title: "Inventario — Structura Market" }] }),
});

interface Movement {
  id: string;
  productName: string | null;
  movementType: string;
  quantityDelta: number;
  quantityAfter: number | null;
  notes: string | null;
  createdAt: string;
}

interface Product {
  id: string;
  name: string;
  sku?: string | null;
  ean?: string | null;
  stockActual: number | null;
}

const TYPE_LABEL: Record<string, string> = {
  adjustment: "Ajuste",
  sale: "Venta",
  sale_reversal: "Reversión venta",
  return: "Devolución",
  receipt: "Ingreso",
};

function MovementsList({ movements, loading }: { movements: Movement[]; loading: boolean }) {
  if (loading) return <Loading variant="list" rows={3} />;
  if (!movements.length) return <CrudEmptyState title="Sin movimientos" />;
  return (
    <ul className="max-h-[560px] space-y-2 overflow-y-auto text-sm">
      {movements.map((m) => (
        <li key={m.id} className="rounded-lg border border-border/60 px-3 py-2">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium">{m.productName ?? "Producto"}</span>
            <span className={m.quantityDelta >= 0 ? "text-emerald-600" : "text-rose-600"}>
              {m.quantityDelta >= 0 ? "+" : ""}
              {m.quantityDelta}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {TYPE_LABEL[m.movementType] ?? m.movementType} · stock {m.quantityAfter ?? "—"} ·{" "}
            {new Date(m.createdAt).toLocaleString("es-AR")}
            {m.notes ? ` · ${m.notes}` : ""}
          </p>
        </li>
      ))}
    </ul>
  );
}

interface IngredientOpt {
  id: string;
  nombre: string;
  stockActual: number;
  unidadBase: string;
}
interface IngredientAdjustment {
  id: string;
  ingredientId: string | null;
  ingredientNombre?: string | null;
  delta: number;
  reason: string;
  notes: string | null;
  createdAt: string;
}

const ING_REASON_LABEL: Record<string, string> = {
  waste: "Merma / descarte",
  manual_sub: "Ajuste (−)",
  manual_add: "Ajuste (+)",
  purchase: "Recepción / compra",
  recipe_consume: "Consumo receta",
  return: "Devolución",
  transfer: "Transferencia",
  sale: "Venta",
};


/**
 * SCRUM-196: Variant × Location matrix table.
 * Rows = variants, Columns = locations. Shows sellable stock qty per cell.
 */
function VariantLocationMatrix({
  matrix,
  locations,
  loading,
}: {
  matrix: Array<{ variantId: string; locationId: string; sellable: number; qtyAvailable: number; qtyReserved: number }>;
  locations: Array<{ id: string; name: string; active: boolean }>;
  loading: boolean;
}) {
  if (loading) return <Loading variant="list" rows={2} />;
  // Build variant list (unique, sorted)
  const variantIds = [...new Set(matrix.map((r) => r.variantId))].sort();
  // Build location set present in matrix
  const matrixLocIds = new Set(matrix.map((r) => r.locationId));
  const cols = locations.filter((l) => matrixLocIds.has(l.id));
  if (!variantIds.length || !cols.length) return null;

  // Index: variantId → locationId → sellable
  const idx = new Map<string, Map<string, number>>();
  for (const r of matrix) {
    if (!idx.has(r.variantId)) idx.set(r.variantId, new Map());
    idx.get(r.variantId)!.set(r.locationId, r.sellable);
  }

  return (
    <div className="mt-2 space-y-1">
      <p className="text-xs font-medium text-muted-foreground">
        Stock por variante × sucursal
      </p>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-xs">
          <thead className="bg-muted/40">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Variante</th>
              {cols.map((l) => (
                <th key={l.id} className="px-3 py-2 text-right font-medium text-muted-foreground whitespace-nowrap">
                  {l.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {variantIds.map((vid) => (
              <tr key={vid} className="hover:bg-muted/20">
                <td className="px-3 py-2 font-mono text-[11px] text-foreground">{vid}</td>
                {cols.map((l) => {
                  const qty = idx.get(vid)?.get(l.id) ?? 0;
                  return (
                    <td key={l.id} className={`px-3 py-2 text-right tabular-nums ${qty === 0 ? "text-muted-foreground" : "text-foreground font-medium"}`}>
                      {qty}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InventarioPage() {
  const tid = useWorkspaceTenantId();
  const contextLocationId = useWorkspaceLocationId();
  const navigate = useNavigate({ from: "/app/inventario" });
  const { tab } = Route.useSearch();
  const tenantCtx = useTenantContextSafe();
  const stockMode = resolveStockControlMode(tenantCtx?.businessProfile, {
    inventoryBom: tenantCtx?.productFlags?.inventory_bom === true,
  });
  const ingredientMode = inventoryControlsIngredients(stockMode);
  const nicheImpact = describeNicheStockImpact(tenantCtx?.businessProfile, {
    inventoryBom: tenantCtx?.productFlags?.inventory_bom === true,
  });
  const hubLinks = getInventarioHubLinks(stockMode);

  const [movements, setMovements] = useState<Movement[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [ingredients, setIngredients] = useState<IngredientOpt[]>([]);
  const [ingAdjustments, setIngAdjustments] = useState<IngredientAdjustment[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [productId, setProductId] = useState("");
  const [productOpen, setProductOpen] = useState(false);
  const [ingredientId, setIngredientId] = useState("");
  const [ingReason, setIngReason] = useState<"waste" | "manual_add" | "manual_sub">("waste");
  const [delta, setDelta] = useState("");
  const [notes, setNotes] = useState("");
  const [csvStock, setCsvStock] = useState("");
  const [importingStock, setImportingStock] = useState(false);
  const [locations, setLocations] = useState<{ id: string; name: string; active: boolean }[]>([]);
  const [adjustLocationId, setAdjustLocationId] = useState("");
  const [variantStockRows, setVariantStockRows] = useState<Array<{ productId: string; variantId: string; sellable: number }>>([]);
  const [selectedVariantId, setSelectedVariantId] = useState("");
  // SCRUM-196: variant × location matrix (cross-location view when product has variants)
  const [variantMatrix, setVariantMatrix] = useState<Array<{ variantId: string; locationId: string; sellable: number; qtyAvailable: number; qtyReserved: number }>>([]);
  const [matrixLoading, setMatrixLoading] = useState(false);

  useEffect(() => {
    if (!tid || !productId) {
      setVariantStockRows([]);
      setSelectedVariantId("");
      return;
    }
    setSelectedVariantId("");
    const loc = adjustLocationId || contextLocationId || "";
    if (!loc) {
      setVariantStockRows([]);
      return;
    }
    let cancelled = false;
    void apiGetJson<{
      variantRows?: Array<{ productId: string; variantId: string; sellable: number }>;
    }>(
      `/api/stock-by-location?locationId=${encodeURIComponent(loc)}&productIds=${encodeURIComponent(productId)}&includeVariants=1`,
      tid,
    )
      .then((r) => {
        if (!cancelled) setVariantStockRows(r.variantRows ?? []);
      })
      .catch(() => {
        if (!cancelled) setVariantStockRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tid, productId, adjustLocationId, contextLocationId]);
  const loadCore = useCallback(() => {
    if (!tid) return;
    setLoading(true);
    const tasks: Promise<unknown>[] = [
      apiGetJson<Movement[]>("/api/inventory/movements", tid).then((m) => setMovements(m)),
      apiGetJson<{ items: Product[] } | Product[]>("/api/products", tid).then((p) =>
        setProducts(Array.isArray(p) ? p : p.items),
      ),
      apiGetJson<{ id: string; name: string; active: boolean }[]>("/api/locations", tid)
        .then((l) => setLocations(l.filter((loc) => loc.active)))
        .catch(() => setLocations([])),
    ];
    if (ingredientMode || stockMode === "hybrid") {
      tasks.push(
        apiGetJson<{ ingredients?: IngredientOpt[] }>("/api/ingredients", tid)
          .then((r) => setIngredients(r.ingredients ?? []))
          .catch(() => setIngredients([])),
        apiGetJson<{ adjustments?: IngredientAdjustment[] }>(
          "/api/stock/adjustment?ingredientOnly=1&limit=80",
          tid,
        )
          .then((r) => setIngAdjustments(r.adjustments ?? []))
          .catch(() => setIngAdjustments([])),
      );
    }
    void Promise.all(tasks)
      .catch(() => toast.error("Error al cargar inventario"))
      .finally(() => setLoading(false));
  }, [tid, ingredientMode, stockMode]);

  useEffect(() => {
    loadCore();
  }, [loadCore]);

  // SCRUM-72: default desde el contexto único de sucursal activa.
  useEffect(() => {
    if (contextLocationId && !adjustLocationId) {
      setAdjustLocationId(contextLocationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextLocationId]);

  // SCRUM-196: load variant × location matrix when a product is selected
  useEffect(() => {
    if (!tid || !productId || locations.length === 0) {
      setVariantMatrix([]);
      return;
    }
    let cancelled = false;
    setMatrixLoading(true);
    void apiGetJson<{
      variantRows?: Array<{ productId: string; variantId: string; locationId: string; sellable: number; qtyAvailable: number; qtyReserved: number }>;
    }>(
      `/api/stock-by-location?productIds=${encodeURIComponent(productId)}&allLocations=1&includeVariants=1`,
      tid,
    )
      .then((r) => {
        if (!cancelled) setVariantMatrix(r.variantRows ?? []);
      })
      .catch(() => { if (!cancelled) setVariantMatrix([]); })
      .finally(() => { if (!cancelled) setMatrixLoading(false); });
    return () => { cancelled = true; };
  }, [tid, productId, locations]);

  const adjustmentMovements = useMemo(
    () => movements.filter((m) => m.movementType === "adjustment"),
    [movements],
  );

  const downloadCsv = async (path: string, filename: string) => {
    if (!tid) return;
    try {
      const res = await fetch(path, { credentials: "include", headers: clientRequestHeaders(tid) });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch {
      toast.error("Error al exportar");
    }
  };

  const downloadEmptyStockTemplate = () => {
    const header = "producto_id,producto_slug,cantidad_delta,stock_nuevo,notas\n";
    const blob = new Blob([header], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "plantilla-inventario.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const readFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => setCsvStock(String(reader.result ?? ""));
    reader.readAsText(file);
  };

  const submitAdjustment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tid || !productId || !delta) return;
    if (variantStockRows.length > 0 && !selectedVariantId) {
      toast.error("Elegí la variante a ajustar");
      return;
    }
    setSaving(true);
    try {
      await apiSendJson(
        "/api/inventory/movements",
        "POST",
        {
          productId,
          ...(selectedVariantId ? { variantId: selectedVariantId } : {}),
          movementType: "adjustment",
          quantityDelta: Number(delta),
          notes: notes || undefined,
          locationId: adjustLocationId || null,
        },
        tid,
      );
      toast.success("Movimiento registrado");
      setDelta("");
      setNotes("");
      loadCore();
    } catch {
      toast.error("No se pudo registrar el movimiento");
    } finally {
      setSaving(false);
    }
  };

  const submitIngredientAdjustment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tid || !ingredientId || !delta) return;
    const qty = Number(delta);
    if (!Number.isFinite(qty) || qty === 0) {
      toast.error("Cantidad inválida");
      return;
    }
    setSaving(true);
    try {
      const signed =
        ingReason === "manual_add" ? Math.abs(qty) : ingReason === "manual_sub" || ingReason === "waste" ? -Math.abs(qty) : qty;
      await apiSendJson(
        "/api/stock/adjustment",
        "POST",
        {
          ingredientId,
          delta: signed,
          reason: ingReason,
          notes: notes || undefined,
          locationId: adjustLocationId || null,
        },
        tid,
      );
      toast.success("Ajuste de insumo registrado");
      setDelta("");
      setNotes("");
      loadCore();
    } catch {
      toast.error("No se pudo registrar el ajuste");
    } finally {
      setSaving(false);
    }
  };

  const importStock = async () => {
    if (!tid || !csvStock.trim()) return;
    setImportingStock(true);
    try {
      const res = await fetch("/api/inventory/import", {
        method: "POST",
        credentials: "include",
        headers: { ...clientRequestHeaders(tid), "Content-Type": "application/json" },
        body: JSON.stringify({ csv: csvStock }),
      });
      const data = (await res.json()) as { applied?: number; errors?: string[]; error?: string };
      if (!res.ok) {
        toast.error(data.error ?? "Importación fallida");
        return;
      }
      toast.success(`${data.applied ?? 0} ajustes de stock aplicados`);
      if (data.errors?.length) toast.message(`${data.errors.length} filas con error`);
      setCsvStock("");
      loadCore();
    } catch {
      toast.error("Error de red");
    } finally {
      setImportingStock(false);
    }
  };

  if (tab === "hub") {
    return (
      <div>
        <InventarioTour />
        <MerchantExperienceBar screenId="inventario" />
      <CrudShellHeader title={inventoryPageTitle(stockMode)} description={inventoryPageSubtitle(stockMode)} />
        {nicheImpact.gap ? (
          <p className="rounded-lg border border-border/60 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
            {nicheImpact.gap}
          </p>
        ) : null}
        <ClinicalPageShell offset="none" className="pt-6">
          <WorkspaceHub
            title={ingredientMode ? "Control de stock de insumos" : "Gestión de inventario"}
            subtitle={
              ingredientMode
                ? "Misma fila de stock que Abastecimiento y BOM: acá registrás mermas y conteos"
                : stockMode === "hybrid"
                  ? "SKU a la venta + insumos si hay receta"
                  : "Importá, ajustá y consultá el ledger de productos (SKU). El desglose por talle/color se edita en el producto."
            }
            links={hubLinks}
          />
        </ClinicalPageShell>
      </div>
    );
  }

  const pageTitle =
    tab === "movimientos"
      ? ingredientMode
        ? "Ajustar insumos"
        : "Ajustar inventario"
      : tab === "stock"
        ? "Importar inventario"
        : tab === "historial"
          ? "Movimientos"
          : tab === "exportar"
            ? "Exportar inventario"
            : tab === "ajustes"
              ? "Historial de ajustes"
              : inventoryPageTitle(stockMode);

  return (
    <div>
      <CrudShellHeader
        title={pageTitle}
        description={
          ingredientMode
            ? "Mermas y conteos sobre la misma fila de stock que usa la receta"
            : "ID y slug para trazabilidad"
        }
        actions={
          <RenataLauncher
            entityType="inventario"
            intent="analyze"
            mode="button"
            label="Analizar stock con Renata"
          />
        }
      />
      <ClinicalPageShell offset="none" className="pt-6">
        <WorkspaceHubBack
          title={inventoryPageTitle(stockMode)}
          onBack={() => void navigate({ search: { tab: "hub" } })}
        />

        {tab === "movimientos" && ingredientMode ? (
          <form
            onSubmit={submitIngredientAdjustment}
            className="mt-4 max-w-lg space-y-4 rounded-xl border border-border bg-card p-5"
          >
            <h2 className="font-semibold text-foreground">Ajuste de insumo</h2>
            <p className="text-xs text-muted-foreground">
              Esto mueve <code className="text-[11px]">stock_actual</code> del ingrediente (mismo dato que
              Abastecimiento y el rinde BOM). Para ingresar compra usá{" "}
              <Link to="/app/compras" search={{ tab: "recepciones", ingredientId: undefined } as never} className="underline">
                Recepciones
              </Link>
              .
            </p>
            <div>
              <Label>Insumo</Label>
              <Select value={ingredientId || undefined} onValueChange={setIngredientId}>
                <SelectTrigger>
                  <SelectValue placeholder="Elegir insumo" />
                </SelectTrigger>
                <SelectContent>
                  {ingredients.map((i) => (
                    <SelectItem key={i.id} value={i.id}>
                      {i.nombre} (stock {i.stockActual} {i.unidadBase})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Motivo</Label>
              <Select
                value={ingReason}
                onValueChange={(v) => setIngReason(v as "waste" | "manual_add" | "manual_sub")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="waste">Merma / descarte</SelectItem>
                  <SelectItem value="manual_sub">Ajuste negativo (conteo)</SelectItem>
                  <SelectItem value="manual_add">Ajuste positivo (conteo)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {locations.length >= 2 && (
              <div>
                <Label>Sucursal</Label>
                <Select value={adjustLocationId || undefined} onValueChange={setAdjustLocationId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Default del comercio" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Cantidad (siempre positiva; el signo lo define el motivo)</Label>
              <Input
                type="number"
                min="0.001"
                step="0.001"
                value={delta}
                onChange={(e) => setDelta(e.target.value)}
                required
              />
            </div>
            <div>
              <Label>Notas</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ej: vencimiento, rotura, inventario físico"
              />
            </div>
            <Button type="submit" disabled={saving || !ingredients.length}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Registrar ajuste"}
            </Button>
          </form>
        ) : null}

        {tab === "movimientos" && !ingredientMode ? (
          <form
            onSubmit={submitAdjustment}
            className="mt-4 max-w-lg space-y-4 rounded-xl border border-border bg-card p-5"
          >
            <h2 className="font-semibold text-foreground">Ajuste manual (SKU)</h2>
            <div>
              <Label>Producto</Label>
              <Popover open={productOpen} onOpenChange={setProductOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={productOpen}
                    className="w-full justify-between font-normal"
                  >
                    {productId
                      ? (products.find((p) => p.id === productId)?.name ?? "Elegir producto")
                      : "Elegir producto — buscar por nombre, SKU o EAN"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder="Buscar por nombre, SKU o EAN…"
                      className="h-9"
                    />
                    <p className="px-3 py-1.5 text-[10px] text-muted-foreground flex items-center gap-1.5">
                      <ScanBarcode className="h-3 w-3" />
                      También podés escanear con pistola — apuntá aquí y disparar
                    </p>
                    <CommandList>
                      <CommandEmpty>Sin resultados</CommandEmpty>
                      <CommandGroup>
                        {products.map((p) => (
                          <CommandItem
                            key={p.id}
                            value={`${p.name} ${p.sku ?? ""} ${p.ean ?? ""}`}
                            onSelect={() => {
                              setProductId(p.id);
                              setProductOpen(false);
                            }}
                          >
                            <Check className={cn("mr-2 h-4 w-4", productId === p.id ? "opacity-100" : "opacity-0")} />
                            <span className="flex-1 min-w-0">
                              <span className="block truncate">{p.name}</span>
                              {(p.sku || p.ean) && (
                                <span className="block text-[10px] text-muted-foreground">
                                  {p.sku ? `SKU: ${p.sku}` : ""}
                                  {p.sku && p.ean ? " · " : ""}
                                  {p.ean ? `EAN: ${p.ean}` : ""}
                                </span>
                              )}
                            </span>
                            {p.stockActual != null && (
                              <span className="ml-2 text-[10px] text-muted-foreground tabular-nums">
                                stock {p.stockActual}
                              </span>
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            {locations.length >= 2 && (
              <div>
                <Label>Sucursal</Label>
                <Select value={adjustLocationId || undefined} onValueChange={setAdjustLocationId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Default del comercio" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {variantStockRows.length > 0 ? (
              <div className="space-y-2">
                <Label>Variante</Label>
                <Select value={selectedVariantId || undefined} onValueChange={setSelectedVariantId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Elegí variante…" />
                  </SelectTrigger>
                  <SelectContent>
                    {variantStockRows.map((v) => (
                      <SelectItem key={v.variantId} value={v.variantId}>
                        {v.variantId} · stock {v.sellable}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            {/* SCRUM-196: Variant × Location matrix — cross-location stock view */}
            {variantMatrix.length > 0 && locations.length > 0 ? (
              <VariantLocationMatrix
                matrix={variantMatrix}
                locations={locations}
                loading={matrixLoading}
              />
            ) : null}
            <div>
              <Label>Cantidad (+ ingreso / − egreso)</Label>
              <Input
                type="number"
                step="0.001"
                value={delta}
                onChange={(e) => setDelta(e.target.value)}
                required
              />
            </div>
            <div>
              <Label>Notas</Label>
              <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Motivo" />
            </div>
            <Button type="submit" disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Registrar movimiento"}
            </Button>
          </form>
        ) : null}

        {tab === "stock" && ingredientMode ? (
          <section className="mt-4 max-w-lg space-y-3 rounded-xl border border-border bg-card p-5">
            <h2 className="font-semibold text-foreground">Importación CSV de productos</h2>
            <p className="text-sm text-muted-foreground">
              En gastronomía el stock principal son los <strong>insumos</strong>. Para cargar compra usá{" "}
              <Link to="/app/compras" search={{ tab: "recepciones", ingredientId: undefined } as never} className="underline">
                Recepciones
              </Link>
              . El CSV de productos queda para bebidas u otros SKUs sin receta.
            </p>
            <Button type="button" variant="outline" onClick={() => void navigate({ search: { tab: "movimientos" } })}>
              Ir a ajustar insumos
            </Button>
          </section>
        ) : null}

        {tab === "stock" && !ingredientMode ? (
          <div className="mt-4 space-y-4 max-w-2xl">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={downloadEmptyStockTemplate}
              >
                <Download className="h-4 w-4" /> Plantilla vacía (id + slug)
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => void downloadCsv("/api/inventory/export", "stock-actual.csv")}
              >
                <Download className="h-4 w-4" /> Exportar stock actual
              </Button>
            </div>
            <section className="rounded-xl border border-border bg-card p-5 space-y-4">
              <p className="text-xs text-muted-foreground font-mono">
                producto_id,producto_slug,cantidad_delta,stock_nuevo,notas
              </p>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-primary hover:underline">
                <Upload className="h-4 w-4" /> Subir .csv
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) readFile(f);
                  }}
                />
              </label>
              <textarea
                className="min-h-[160px] w-full rounded-lg border border-input bg-background px-3 py-2 text-sm font-mono"
                value={csvStock}
                onChange={(e) => setCsvStock(e.target.value)}
              />
              <Button type="button" disabled={importingStock} onClick={() => void importStock()}>
                {importingStock ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aplicar importación"}
              </Button>
            </section>
          </div>
        ) : null}

        {tab === "historial" && (
          <section className="mt-4 rounded-xl border border-border bg-card p-5">
            <h2 className="mb-4 font-semibold text-foreground">
              {ingredientMode ? "Movimientos de insumos" : "Todos los movimientos"}
            </h2>
            {ingredientMode ? (
              loading ? (
                <Loading variant="list" rows={3} />
              ) : !ingAdjustments.length ? (
                <CrudEmptyState title="Sin movimientos de insumos" />
              ) : (
                <ul className="max-h-[560px] space-y-2 overflow-y-auto text-sm">
                  {ingAdjustments.map((a) => (
                    <li key={a.id} className="rounded-lg border border-border/60 px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium">{a.ingredientNombre ?? a.ingredientId ?? "Insumo"}</span>
                        <span className={a.delta >= 0 ? "text-emerald-600" : "text-rose-600"}>
                          {a.delta >= 0 ? "+" : ""}
                          {a.delta}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {ING_REASON_LABEL[a.reason] ?? a.reason} ·{" "}
                        {new Date(a.createdAt).toLocaleString("es-AR")}
                        {a.notes ? ` · ${a.notes}` : ""}
                      </p>
                    </li>
                  ))}
                </ul>
              )
            ) : (
              <MovementsList movements={movements} loading={loading} />
            )}
          </section>
        )}

        {tab === "ajustes" && (
          <section className="mt-4 rounded-xl border border-border bg-card p-5">
            <h2 className="mb-4 font-semibold text-foreground">
              {ingredientMode ? "Solo mermas y ajustes manuales" : "Solo ajustes manuales"}
            </h2>
            {ingredientMode ? (
              loading ? (
                <Loading variant="list" rows={3} />
              ) : (
                <ul className="max-h-[560px] space-y-2 overflow-y-auto text-sm">
                  {ingAdjustments
                    .filter((a) => a.reason === "waste" || a.reason === "manual_add" || a.reason === "manual_sub")
                    .map((a) => (
                      <li key={a.id} className="rounded-lg border border-border/60 px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium">{a.ingredientNombre ?? "Insumo"}</span>
                          <span className={a.delta >= 0 ? "text-emerald-600" : "text-rose-600"}>
                            {a.delta >= 0 ? "+" : ""}
                            {a.delta}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {ING_REASON_LABEL[a.reason] ?? a.reason} ·{" "}
                          {new Date(a.createdAt).toLocaleString("es-AR")}
                        </p>
                      </li>
                    ))}
                </ul>
              )
            ) : (
              <MovementsList movements={adjustmentMovements} loading={loading} />
            )}
          </section>
        )}

        {tab === "exportar" && (
          <section className="mt-4 max-w-lg space-y-4 rounded-xl border border-border bg-card p-5">
            <h2 className="font-semibold text-foreground">
              {ingredientMode ? "Exportar stock de productos (opcional)" : "Exportar inventario"}
            </h2>
            <p className="text-sm text-muted-foreground">
              {ingredientMode ? (
                <>
                  El stock operativo de cocina está en{" "}
                  <Link to="/app/ingredientes" className="underline">
                    Insumos
                  </Link>
                  . Este CSV es para productos/SKU sin receta.
                </>
              ) : (
                <>
                  Descargá el stock actual con <strong>producto_id</strong> y <strong>producto_slug</strong> para editar
                  y reimportar con integridad.
                </>
              )}
            </p>
            <Button
              type="button"
              className="gap-2"
              onClick={() => void downloadCsv("/api/inventory/export", "inventario-export.csv")}
            >
              <Download className="h-4 w-4" /> Descargar CSV de stock
            </Button>
          </section>
        )}
      </ClinicalPageShell>
    </div>
  );
}
